/**
 * @fileoverview Main Operator Administration Panel for Tug of War (Estira y Afloja).
 * Allows stadium operators to project cover/game scenes, configure game rules (modes, physics, duration, overtime),
 * monitor authoritative real-time rope telemetry at 100ms, manage themes, and view match history.
 * 
 * @module components/Admin/Games/TugOfWar/TugOfWar
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { SecondaryBase } from '../../Commons/SecondaryBase';
import { Confirm } from '../../../Commons/Modals/Confirm';
import { PremiumQR } from '../../../Commons/PremiumQR';
import { TugOfWarThemeModal } from './TugOfWarThemeModal';

import {
    GetThemes,
    GetConfig,
    SaveConfig,
    GetMatchesHistory,
    DeleteTheme,
    CloneTheme,
    ShowCover,
    ShowGame,
    StartGame,
    FinishEarly,
    ResetGame,
    GetRealtimeState,
    CalculatePullPercentage,
    DeterminePhaseTier,
    ResolveMediaUrl
} from '../../../../utilities/BL/TugOfWar';

import {
    ConnectWebSocketForTugOfWarAdmin,
    DisconnectWebSocketForTugOfWarAdmin
} from '../../../../utilities/WS/Admin/TugOfWarWS';

/**
 * Panel de Administración y Control del Operador para Estira y Afloja (Tug of War).
 * 
 * @function TugOfWar
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.getOutOfTheDynamic - Callback para salir de la dinámica al Dashboard.
 * @param {string} [props.roomId] - Identificador GUID de la sala activa.
 * @returns {React.ReactElement} Panel del operador completo.
 */
export const TugOfWar = ({ getOutOfTheDynamic, roomId: propRoomId }) => {
    const { showMessage } = useFloatMessage();

    // Detección segura de Sala SaaS
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);

    const activeRoomId = propRoomId ||
        searchParams.get('roomId') ||
        (typeof localStorage !== 'undefined'
            ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
            : '') ||
        '';

    // Estados de carga y WebSocket
    const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
    const [lastRefreshTimestampString, setLastRefreshTimestampString] = useState('');
    const [webSocketConnectionStatus, setWebSocketConnectionStatus] = useState('connected');

    // Catálogo de temas y configuración
    const [availableThemesList, setAvailableThemesList] = useState([]);
    const [selectedThemeId, setSelectedThemeId] = useState(0);
    const [activeThemeObject, setActiveThemeObject] = useState(null);
    const activeThemeObjectRef = useRef(activeThemeObject);

    useEffect(() => {
        activeThemeObjectRef.current = activeThemeObject;
    }, [activeThemeObject]);

    const [roomConfigState, setRoomConfigState] = useState({
        roundDuration: 15,
        gameMode: 'Timed', // 'Timed' | 'SuddenDeath'
        displacementStyle: 'DynamicBias', // 'DynamicBias' | 'VisualSuspense'
        enableOvertimeOnDraw: false
    });
    const [isAdvancedConfigOpen, setIsAdvancedConfigOpen] = useState(false);

    // Estado de telemetría y juego en vivo
    const [currentGameState, setCurrentGameState] = useState({
        state: 'idle', // idle, cover, game_ready, countdown, playing, sudden_death_critical, paused, finished
        phase: 'idle',
        roundDuration: 15,
        remainingSeconds: 15,
        scoreJ1: 0,
        scoreJ2: 0,
        totalTapsJ1: 0,
        totalTapsJ2: 0,
        activeFansJ1: 0,
        activeFansJ2: 0,
        ropeDisplacementVw: 0,
        tierJ1: 1,
        tierJ2: 1,
        winner: null,
        wasSuddenDeathTriggered: false,
        wasEmergencyStopped: false
    });

    // Cuenta regresiva
    const [countdownSecondsRemaining, setCountdownSecondsRemaining] = useState(null);

    // Histórico de partidas
    const [matchesHistoryList, setMatchesHistoryList] = useState([]);

    // Estados de Modales
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmModalConfiguration, setConfirmModalConfiguration] = useState({
        message: '',
        actionToExecute: null,
        yesButtonText: 'Sí, confirmar',
        noButtonText: 'Cancelar'
    });
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [themeToEditObject, setThemeToEditObject] = useState(null);
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [fanJoinQrUrl, setFanJoinQrUrl] = useState('');
    const [screenTargetDisplayUrl, setScreenTargetDisplayUrl] = useState('');

    /**
     * Resuelve URLs multimedia para previsualización.
     */
    const resolveMediaUrl = (mediaPath) => {
        return ResolveMediaUrl(mediaPath);
    };

    /**
     * Manejador de eventos en tiempo real recibidos vía WebSocket / SignalR.
     */
    const handleWebSocketMessageReceived = useCallback((receivedPacket) => {
        if (!receivedPacket || typeof receivedPacket !== 'object') return;

        const packetType = receivedPacket.type ? String(receivedPacket.type).toLowerCase() : '';
        const data = receivedPacket.state || receivedPacket.data || receivedPacket.payload || receivedPacket;

        switch (packetType) {
            case 'tugofwar_telemetry':
            case 'telemetry': {
                const s1 = data.scoreJ1 ?? data.ScoreJ1 ?? data.player1?.score ?? data.Player1?.Score;
                const s2 = data.scoreJ2 ?? data.ScoreJ2 ?? data.player2?.score ?? data.Player2?.Score;
                const t1 = data.totalTapsJ1 ?? data.TotalTapsJ1 ?? data.player1?.totalTaps ?? data.Player1?.TotalTaps;
                const t2 = data.totalTapsJ2 ?? data.TotalTapsJ2 ?? data.player2?.totalTaps ?? data.Player2?.TotalTaps;
                const f1 = data.activeFansJ1 ?? data.ActiveFansJ1 ?? data.player1?.activeFans ?? data.Player1?.ActiveFans;
                const f2 = data.activeFansJ2 ?? data.ActiveFansJ2 ?? data.player2?.activeFans ?? data.Player2?.ActiveFans;
                const disp = data.ropeDisplacementVw ?? data.RopeDisplacementVw ?? data.displacement ?? data.ropePosition;
                const tier1 = data.tierJ1 ?? data.TierJ1 ?? data.player1?.tier;
                const tier2 = data.tierJ2 ?? data.TierJ2 ?? data.player2?.tier;
                const rem = data.secondsRemaining ?? data.SecondsRemaining ?? (typeof data.remainingMs === 'number'
                    ? Math.max(0, Math.round((data.remainingMs / 1000) * 10) / 10)
                    : (typeof data.remainingSeconds === 'number' ? data.remainingSeconds : null));

                const incomingPhase = data.phase || data.state || '';

                setCurrentGameState((previousState) => {
                    const resolvedPhase = incomingPhase === 'playing' || previousState.state === 'countdown'
                        ? 'playing'
                        : (incomingPhase || previousState.state);

                    return {
                        ...previousState,
                        state: resolvedPhase,
                        phase: resolvedPhase,
                        scoreJ1: s1 !== undefined ? Number(s1) : previousState.scoreJ1,
                        scoreJ2: s2 !== undefined ? Number(s2) : previousState.scoreJ2,
                        totalTapsJ1: t1 !== undefined ? Number(t1) : previousState.totalTapsJ1,
                        totalTapsJ2: t2 !== undefined ? Number(t2) : previousState.totalTapsJ2,
                        activeFansJ1: f1 !== undefined ? Number(f1) : previousState.activeFansJ1,
                        activeFansJ2: f2 !== undefined ? Number(f2) : previousState.activeFansJ2,
                        ropeDisplacementVw: disp !== undefined ? Number(disp) : previousState.ropeDisplacementVw,
                        tierJ1: tier1 !== undefined ? Number(tier1) : previousState.tierJ1,
                        tierJ2: tier2 !== undefined ? Number(tier2) : previousState.tierJ2,
                        remainingSeconds: rem !== null && rem !== undefined ? Number(rem) : previousState.remainingSeconds
                    };
                });
                break;
            }

            case 'tugofwar_countdown_tick':
            case 'tugofwar_countdown_started':
            case 'tugofwar_countdown': {
                const countVal = data.countdown ?? data.count ?? data.secondsRemaining ?? data.countdownSeconds ?? 3;
                setCountdownSecondsRemaining(countVal);
                setCurrentGameState((prev) => ({
                    ...prev,
                    state: 'countdown',
                    phase: 'countdown',
                    winner: null
                }));
                break;
            }

            case 'tugofwar_round_started':
            case 'tugofwar_match_started': {
                setCountdownSecondsRemaining(null);
                setCurrentGameState((prev) => ({
                    ...prev,
                    state: 'playing',
                    phase: 'playing',
                    winner: null,
                    remainingSeconds: data.durationSeconds || prev.roundDuration || 15
                }));
                break;
            }

            case 'tugofwar_sudden_death_started':
            case 'tugofwar_sudden_death': {
                setCurrentGameState((prev) => ({
                    ...prev,
                    state: 'sudden_death_critical',
                    phase: 'sudden_death_critical',
                    wasSuddenDeathTriggered: true
                }));
                break;
            }

            case 'tugofwar_game_over':
            case 'tugofwar_finished': {
                setCountdownSecondsRemaining(null);
                const rawWinner = data.winner || receivedPacket.winner || 'Empate';
                const currentTheme = activeThemeObjectRef.current;
                const finalWinner = (rawWinner === 'player1' || rawWinner === 'J1')
                    ? (currentTheme?.player1Name || 'Equipo 1')
                    : ((rawWinner === 'player2' || rawWinner === 'J2')
                        ? (currentTheme?.player2Name || 'Equipo 2')
                        : rawWinner);

                setCurrentGameState((prev) => ({
                    ...prev,
                    state: 'finished',
                    phase: 'finished',
                    winner: finalWinner,
                    remainingSeconds: 0,
                    scoreJ1: data.scoreJ1 ?? data.ScoreJ1 ?? prev.scoreJ1,
                    scoreJ2: data.scoreJ2 ?? data.ScoreJ2 ?? prev.scoreJ2,
                    totalTapsJ1: data.totalTapsJ1 ?? data.TotalTapsJ1 ?? prev.totalTapsJ1,
                    totalTapsJ2: data.totalTapsJ2 ?? data.TotalTapsJ2 ?? prev.totalTapsJ2
                }));
                loadMatchesHistory();
                break;
            }

            case 'tugofwar_cover_shown':
            case 'tugofwar_show_cover':
                setCountdownSecondsRemaining(null);
                setCurrentGameState((prev) => ({ ...prev, state: 'cover', phase: 'cover' }));
                break;

            case 'tugofwar_game_scene_shown':
            case 'tugofwar_game_ready':
                setCountdownSecondsRemaining(null);
                setCurrentGameState((prev) => ({
                    ...prev,
                    state: 'game_ready',
                    phase: 'game_ready',
                    winner: null,
                    scoreJ1: 0,
                    scoreJ2: 0,
                    totalTapsJ1: 0,
                    totalTapsJ2: 0,
                    ropeDisplacementVw: 0,
                    remainingSeconds: data.durationSeconds || prev.roundDuration || 15
                }));
                break;

            case 'tugofwar_match_paused':
            case 'tugofwar_paused':
                setCurrentGameState((prev) => ({ ...prev, state: 'paused', phase: 'paused' }));
                break;

            case 'tugofwar_match_resumed':
            case 'tugofwar_resumed':
                setCurrentGameState((prev) => ({ ...prev, state: 'playing', phase: 'playing' }));
                break;

            case 'tugofwar_match_reset':
            case 'tugofwar_reset':
                setCountdownSecondsRemaining(null);
                setCurrentGameState((prev) => ({
                    ...prev,
                    state: 'idle',
                    phase: 'idle',
                    winner: null,
                    scoreJ1: 0,
                    scoreJ2: 0,
                    totalTapsJ1: 0,
                    totalTapsJ2: 0,
                    ropeDisplacementVw: 0,
                    remainingSeconds: prev.roundDuration || 15
                }));
                break;

            case 'tugofwar_state_changed':
            case 'tugofwar_snapshot':
                if (data.phase || data.state) {
                    const rawPhase = data.phase || data.state;
                    setCurrentGameState((prev) => ({
                        ...prev,
                        ...data,
                        state: rawPhase,
                        phase: rawPhase
                    }));
                }
                break;

            default:
                break;
        }
    }, []);

    /**
     * Carga inicial y suscripción a WebSocket.
     */
    useEffect(() => {
        loadAllInitialAdminData();

        ConnectWebSocketForTugOfWarAdmin(
            handleWebSocketMessageReceived,
            (newConnectionStatus) => setWebSocketConnectionStatus(newConnectionStatus),
            activeRoomId
        );

        return () => {
            DisconnectWebSocketForTugOfWarAdmin();
        };
    }, [activeRoomId]);

    /**
     * Carga catálogo de temas, configuración, telemetría e histórico de partidas.
     */
    const loadAllInitialAdminData = async () => {
        setIsLoadingInitialData(true);
        try {
            // 1. Obtener Temas
            const themesResponse = await GetThemes(activeRoomId);
            let currentThemes = [];
            if (themesResponse.success && Array.isArray(themesResponse.data)) {
                currentThemes = themesResponse.data;
                setAvailableThemesList(currentThemes);
            }

            // 2. Obtener Configuración de Sala
            const configResponse = await GetConfig(activeRoomId);
            if (configResponse.success && configResponse.data) {
                const configData = configResponse.data;
                setRoomConfigState({
                    roundDuration: configData.roundDurationSeconds || configData.roundDuration || 15,
                    gameMode: configData.gameMode || 'Timed',
                    displacementStyle: configData.displacementStyle || 'DynamicBias',
                    enableOvertimeOnDraw: Boolean(configData.enableOvertimeOnDraw)
                });

                if (configData.activeThemeId) {
                    setSelectedThemeId(configData.activeThemeId);
                    const matchingTheme = currentThemes.find((t) => t.id === configData.activeThemeId);
                    if (matchingTheme) setActiveThemeObject(matchingTheme);
                } else if (currentThemes.length > 0) {
                    setSelectedThemeId(currentThemes[0].id);
                    setActiveThemeObject(currentThemes[0]);
                }

                if (configData.activeTheme) {
                    setActiveThemeObject(configData.activeTheme);
                }
            } else if (currentThemes.length > 0) {
                setSelectedThemeId(currentThemes[0].id);
                setActiveThemeObject(currentThemes[0]);
            }

            // 3. Obtener Estado en Tiempo Real
            const realtimeResponse = await GetRealtimeState(activeRoomId);
            if (realtimeResponse.success && realtimeResponse.data) {
                setCurrentGameState((prev) => ({
                    ...prev,
                    ...realtimeResponse.data,
                    state: realtimeResponse.data.phase || realtimeResponse.data.state || prev.state
                }));
                if (realtimeResponse.data.currentTheme) {
                    setActiveThemeObject(realtimeResponse.data.currentTheme);
                }
            }

            // 4. Cargar Histórico
            await loadMatchesHistory();
        } catch (initializationError) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cargar los datos del juego Tug of War.' });
        } finally {
            setIsLoadingInitialData(false);
            setLastRefreshTimestampString(
                new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
            );
        }
    };

    /**
     * Carga el historial de partidas desde la base de datos.
     */
    const loadMatchesHistory = async () => {
        if (!activeRoomId) return;
        try {
            const historyResponse = await GetMatchesHistory(activeRoomId, 20);
            if (historyResponse.success && Array.isArray(historyResponse.data)) {
                setMatchesHistoryList(historyResponse.data);
            }
        } catch (historyException) {
            // No bloqueante
        }
    };

    /**
     * Valida que exista un tema seleccionado antes de ejecutar comandos de pantalla o partida.
     */
    const checkThemeSelected = () => {
        if (!selectedThemeId || Number(selectedThemeId) === 0 || selectedThemeId === '') {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Selecciona un tema para continuar.' });
            return false;
        }
        return true;
    };

    /**
     * Actualiza el tema activo en la configuración y persiste en la base de datos.
     */
    const handleActiveThemeSelectionChange = async (event) => {
        const rawValue = event.target.value;
        const newlySelectedId = rawValue ? Number(rawValue) : 0;
        setSelectedThemeId(newlySelectedId);

        const foundTheme = availableThemesList.find((themeItem) => themeItem.id === newlySelectedId);
        setActiveThemeObject(foundTheme || null);

        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando tema activo...' });
        const saveConfigResponse = await SaveConfig({
            roomId: activeRoomId,
            activeThemeId: newlySelectedId > 0 ? newlySelectedId : null,
            roundDuration: roomConfigState.roundDuration,
            roundDurationSeconds: roomConfigState.roundDuration,
            gameMode: roomConfigState.gameMode,
            displacementStyle: roomConfigState.displacementStyle,
            enableOvertimeOnDraw: roomConfigState.enableOvertimeOnDraw
        }, activeRoomId);

        if (saveConfigResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema activo actualizado correctamente.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: saveConfigResponse.message || 'Error al actualizar el tema.' });
        }
    };

    /**
     * Guarda un cambio en los parámetros de configuración de la partida.
     */
    const handleConfigParameterChange = async (parameterName, parameterValue) => {
        const updatedConfig = {
            ...roomConfigState,
            [parameterName]: parameterValue
        };
        setRoomConfigState(updatedConfig);

        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Guardando configuración...' });
        const saveConfigResponse = await SaveConfig({
            roomId: activeRoomId,
            activeThemeId: selectedThemeId > 0 ? selectedThemeId : null,
            roundDuration: updatedConfig.roundDuration,
            roundDurationSeconds: updatedConfig.roundDuration,
            gameMode: updatedConfig.gameMode,
            displacementStyle: updatedConfig.displacementStyle,
            enableOvertimeOnDraw: updatedConfig.enableOvertimeOnDraw
        }, activeRoomId);

        if (saveConfigResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Configuración guardada.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: saveConfigResponse.message || 'Error al guardar configuración.' });
        }
    };

    // =========================================================================
    // COMANDOS DE PROYECCIÓN
    // =========================================================================

    const handleShowCoverAction = async () => {
        if (!checkThemeSelected()) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Proyectando Portada Promocional...' });
        const coverResponse = await ShowCover(activeRoomId, selectedThemeId);
        if (coverResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Portada emitida a pantalla gigante.' });
            const incoming = coverResponse.data || coverResponse.state;
            if (incoming) setCurrentGameState((prev) => ({ ...prev, ...incoming, state: 'cover' }));
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: coverResponse.message || 'Error al emitir portada.' });
        }
    };

    const handleShowGameAction = async () => {
        if (!checkThemeSelected()) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Proyectando Escenario de Estira y Afloja...' });
        const gameResponse = await ShowGame(
            activeRoomId,
            selectedThemeId,
            roomConfigState.gameMode,
            roomConfigState.displacementStyle,
            roomConfigState.roundDuration,
            roomConfigState.enableOvertimeOnDraw
        );
        if (gameResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Escenario de juego proyectado en pantalla.' });
            const incoming = gameResponse.data || gameResponse.state;
            if (incoming) setCurrentGameState((prev) => ({ ...prev, ...incoming, state: 'game_ready' }));
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: gameResponse.message || 'Error al proyectar juego.' });
        }
    };

    const handleOpenQrModal = () => {
        if (!checkThemeSelected()) return;
        const currentHostname = window.location.hostname;
        const currentPort = window.location.port ? `:${window.location.port}` : '';
        const currentProtocol = window.location.protocol;
        const roomQuery = activeRoomId ? `?roomId=${encodeURIComponent(activeRoomId)}` : '';

        const fanUrl = `${currentProtocol}//${currentHostname}${currentPort}/tugofwar${roomQuery}`;
        const screenUrl = `${currentProtocol}//${currentHostname}${currentPort}/screen${roomQuery}`;

        setFanJoinQrUrl(fanUrl);
        setScreenTargetDisplayUrl(screenUrl);
        setIsQrModalOpen(true);
    };

    // =========================================================================
    // COMANDOS DE PARTIDA
    // =========================================================================

    const handleStartGameAction = async () => {
        if (!checkThemeSelected()) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Iniciando partida con cuenta regresiva (3, 2, 1)...' });
        const startResponse = await StartGame(activeRoomId);
        if (startResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡Partida de Estira y Afloja iniciada!' });
            const incoming = startResponse.data || startResponse.state;
            if (incoming) setCurrentGameState((prev) => ({ ...prev, ...incoming }));
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: startResponse.message || 'Error al iniciar partida.' });
        }
    };

    const handleConfirmFinishEarly = () => {
        setConfirmModalConfiguration({
            message: '¿Estás seguro de finalizar la partida antes de tiempo? Se declarará ganador de inmediato con la tracción actual de la soga.',
            yesButtonText: 'Sí, finalizar ahora',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Deteniendo partida de emergencia...' });
                const finishResponse = await FinishEarly(activeRoomId);
                if (finishResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Partida concluida anticipadamente.' });
                    const incoming = finishResponse.data || finishResponse.state;
                    if (incoming) setCurrentGameState((prev) => ({ ...prev, ...incoming, state: 'finished' }));
                    await loadMatchesHistory();
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: finishResponse.message || 'Error al finalizar partida.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    const handleConfirmResetMatch = () => {
        setConfirmModalConfiguration({
            message: '¿Deseas reiniciar la partida a su estado inicial? Los toques y posiciones se restablecerán.',
            yesButtonText: 'Sí, reiniciar',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reiniciando partida...' });
                const resetResponse = await ResetGame(activeRoomId);
                if (resetResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Partida reiniciada.' });
                    setCurrentGameState((prev) => ({
                        ...prev,
                        state: 'idle',
                        phase: 'idle',
                        scoreJ1: 0,
                        scoreJ2: 0,
                        totalTapsJ1: 0,
                        totalTapsJ2: 0,
                        ropeDisplacementVw: 0,
                        winner: null
                    }));
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: resetResponse.message || 'Error al reiniciar.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    // =========================================================================
    // ACCIONES CRUD DE TEMAS
    // =========================================================================

    const handleOpenCreateThemeModal = () => {
        setThemeToEditObject(null);
        setIsThemeModalOpen(true);
    };

    const handleOpenEditThemeModal = () => {
        const foundTheme = availableThemesList.find((item) => item.id === selectedThemeId);
        setThemeToEditObject(foundTheme || null);
        setIsThemeModalOpen(true);
    };

    const handleCloneSelectedTheme = async () => {
        if (!selectedThemeId || selectedThemeId <= 0) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Clonando tema...' });
        try {
            const cloneResponse = await CloneTheme(selectedThemeId);
            if (cloneResponse.success && cloneResponse.data) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema clonado exitosamente.' });
                await loadAllInitialAdminData();
                setSelectedThemeId(cloneResponse.data.id);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: cloneResponse.message || 'Error al clonar tema.' });
            }
        } catch (cloneError) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al clonar tema.' });
        }
    };

    const handleConfirmDeleteTheme = () => {
        if (!selectedThemeId || selectedThemeId <= 0) return;
        const currentTheme = availableThemesList.find((t) => t.id === selectedThemeId);
        const themeName = currentTheme ? currentTheme.name : 'este tema';

        setConfirmModalConfiguration({
            message: `¿Estás seguro de que deseas eliminar permanentemente el tema "${themeName}"?`,
            yesButtonText: 'Sí, eliminar',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Eliminando tema...' });
                const deleteResponse = await DeleteTheme(selectedThemeId);
                if (deleteResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema eliminado correctamente.' });
                    await loadAllInitialAdminData();
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: deleteResponse.message || 'Error al eliminar.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    /**
     * Renderiza el badge del estado actual del juego.
     */
    const renderGameStateBadge = () => {
        const gameStateKey = currentGameState.state || currentGameState.phase;

        switch (gameStateKey) {
            case 'idle':
                return <span className="badge bg-secondary fs-6 px-3 py-2">En Espera (Idle)</span>;
            case 'cover':
                return <span className="badge bg-info text-dark fs-6 px-3 py-2">🖼️ Proyectando Portada</span>;
            case 'game_ready':
                return <span className="badge bg-primary fs-6 px-3 py-2">🏟️ Escenario Listo</span>;
            case 'countdown':
                return (
                    <span className="badge bg-warning text-dark fs-6 px-3 py-2 animate-pulse">
                        ⏳ Cuenta Regresiva: {countdownSecondsRemaining ?? 3}s
                    </span>
                );
            case 'playing':
                return <span className="badge bg-danger fs-6 px-3 py-2 animate-pulse">🔥 ¡JALANDO LA CUERDA!</span>;
            case 'sudden_death_critical':
            case 'sudden_death':
                return <span className="badge bg-warning text-dark fs-6 px-3 py-2 animate-pulse">⚡ ¡MUERTE SÚBITA!</span>;
            case 'paused':
                return <span className="badge bg-warning text-dark fs-6 px-3 py-2">⏸️ Partida Pausada</span>;
            case 'finished':
                return (
                    <span className="badge bg-success fs-6 px-3 py-2">
                        🏆 ¡Finalizado! Ganador: {currentGameState.winner || 'Empate'}
                    </span>
                );
            default:
                return <span className="badge bg-secondary fs-6 px-3 py-2">{gameStateKey}</span>;
        }
    };

    // Cálculos de tracción y fuerza en tiempo real con fallbacks defensivos
    const pullProportions = (typeof CalculatePullPercentage === 'function'
        ? CalculatePullPercentage(currentGameState.scoreJ1, currentGameState.scoreJ2)
        : null) || { percentage1: 50, percentage2: 50 };
    const tierJ1 = currentGameState.tierJ1 || (typeof DeterminePhaseTier === 'function' ? DeterminePhaseTier(pullProportions.percentage1) : 1);
    const tierJ2 = currentGameState.tierJ2 || (typeof DeterminePhaseTier === 'function' ? DeterminePhaseTier(pullProportions.percentage2) : 1);

    const isMatchActive = currentGameState.state === 'playing' ||
        currentGameState.state === 'countdown' ||
        currentGameState.state === 'sudden_death_critical';

    const team1Color = activeThemeObject?.player1Color || '#0284c7';
    const team2Color = activeThemeObject?.player2Color || '#ef4444';
    const team1Name = activeThemeObject?.player1Name || 'EQUIPO 1';
    const team2Name = activeThemeObject?.player2Name || 'EQUIPO 2';

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>
            {/* Modal de Confirmación */}
            {isConfirmModalOpen && (
                <Confirm
                    message={confirmModalConfiguration.message}
                    actionToYesButton={confirmModalConfiguration.actionToExecute}
                    textYesButton={confirmModalConfiguration.yesButtonText}
                    textNoButton={confirmModalConfiguration.noButtonText}
                    setShowModal={setIsConfirmModalOpen}
                />
            )}

            {/* Modal de Códigos QR y Enlaces */}
            {isQrModalOpen && (
                <>
                    <div className="cModal__Background" onClick={() => setIsQrModalOpen(false)}></div>
                    <div className="cModal__Container" style={{ zIndex: 99999 }}>
                        <div
                            className="cModal__Container__Content position-relative p-0 overflow-hidden"
                            style={{ background: 'transparent', boxShadow: 'none', maxWidth: '440px' }}
                        >
                            <button
                                type="button"
                                className="btn btn-sm btn-dark position-absolute top-0 end-0 m-3 rounded-circle p-2"
                                onClick={() => setIsQrModalOpen(false)}
                                style={{ zIndex: 20 }}
                                title="Cerrar modal"
                            >
                                <i className="bi bi-x-lg text-white"></i>
                            </button>
                            <div className="bg-dark text-white p-3 rounded-top-4 text-center border-bottom border-secondary">
                                <span className="d-block small text-warning mb-1">
                                    <i className="bi bi-tv me-1"></i> Pantalla Gigante Estadio / Overlay:
                                </span>
                                <a
                                    href={screenTargetDisplayUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="fw-bold text-info small text-break text-decoration-none d-block"
                                >
                                    {screenTargetDisplayUrl} <i className="bi bi-box-arrow-up-right ms-1"></i>
                                </a>
                            </div>
                            <PremiumQR url={fanJoinQrUrl} title="ESTIRA Y AFLOJA" />
                        </div>
                    </div>
                </>
            )}

            {/* Modal de Gestión de Temas */}
            {isThemeModalOpen && (
                <TugOfWarThemeModal
                    isOpen={isThemeModalOpen}
                    initialThemeData={themeToEditObject}
                    roomId={activeRoomId}
                    onCloseModal={() => setIsThemeModalOpen(false)}
                    onThemeSavedSuccessfully={(savedTheme) => {
                        loadAllInitialAdminData();
                        if (savedTheme?.id) setSelectedThemeId(savedTheme.id);
                    }}
                />
            )}

            {/* HEADER DEL PANEL */}
            <div className="mb-4 text-center text-dark">
                <div className="d-flex align-items-center justify-content-center gap-2">
                    <i className="bi bi-arrows-collapse text-warning fs-1"></i>
                    <h2 className="text-uppercase fw-bold m-0" style={{ letterSpacing: '2px' }}>
                        Estira y Afloja (Tug of War)
                    </h2>
                </div>
                <div className="d-inline-flex align-items-center gap-3 bg-white px-3 py-1 rounded-pill border shadow-sm mt-2 flex-wrap">
                    <span className="small">
                        {webSocketConnectionStatus === 'connected' && <strong className="text-success">🟢 En Línea</strong>}
                        {webSocketConnectionStatus === 'connecting' && <strong className="text-warning">🟡 Conectando...</strong>}
                        {webSocketConnectionStatus === 'reconnecting' && <strong className="text-warning">🟡 Reconectando...</strong>}
                        {(webSocketConnectionStatus === 'error' || webSocketConnectionStatus === 'closed') && (
                            <strong className="text-danger">🔴 Desconectado</strong>
                        )}
                    </span>
                    <span className="small text-muted border-start ps-3">
                        Actualizado: <strong className="text-dark">{lastRefreshTimestampString}</strong>
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-light border rounded-circle p-1 d-flex align-items-center justify-content-center"
                        onClick={loadAllInitialAdminData}
                        title="Refrescar estado"
                        style={{ width: '28px', height: '28px' }}
                    >
                        <i className={`bi bi-arrow-repeat text-primary ${isLoadingInitialData ? 'spinner-border spinner-border-sm border-0' : ''}`}></i>
                    </button>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 1: TARJETAS DE PROYECCIÓN Y CONTROL DE PARTIDA         */}
            {/* ============================================================ */}
            <div className="row g-4 mb-4">
                {/* TARJETA 1: PROYECCIÓN Y ENLACES */}
                <div className="col-lg-5">
                    <div className="card shadow-sm border-0 rounded-4 h-100 overflow-hidden">
                        <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                            <h5 className="m-0 fw-bold">
                                <i className="bi bi-projector-fill text-warning me-2"></i> 1. Proyección en Estadio
                            </h5>
                            <span className="badge bg-primary">Vistas</span>
                        </div>
                        <div className="card-body bg-light d-flex flex-column justify-content-between p-4">
                            <p className="small text-muted mb-3">
                                Alterna instantáneamente lo que se proyecta en la pantalla gigante (/screen) entre la Portada de bienvenida y el Escenario interactivo de la Cuerda.
                            </p>
                            <div className="d-grid gap-3">
                                <button
                                    type="button"
                                    className="btn btn-info fw-bold py-2 text-dark shadow-sm"
                                    onClick={handleShowCoverAction}
                                >
                                    <i className="bi bi-image me-1"></i> Proyectar Portada
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary fw-bold py-2 text-white shadow-sm"
                                    onClick={handleShowGameAction}
                                >
                                    <i className="bi bi-tv-fill me-1"></i> Proyectar Pantalla de Juego
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-dark fw-bold py-2 shadow-sm"
                                    onClick={handleOpenQrModal}
                                >
                                    <i className="bi bi-qr-code-scan me-1"></i> Código QR e Instrucciones
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TARJETA 2: CONTROL DE PARTIDA */}
                <div className="col-lg-7">
                    <div className="card shadow-sm border-0 rounded-4 h-100 overflow-hidden">
                        <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                            <h5 className="m-0 fw-bold">
                                <i className="bi bi-controller text-danger me-2"></i> 2. Control de Partida
                            </h5>
                            {renderGameStateBadge()}
                        </div>
                        <div className="card-body bg-light p-4">
                            {/* Selector de Tema Activo */}
                            <div className="mb-3">
                                <label className="form-label fw-bold small text-muted">
                                    <i className="bi bi-palette-fill me-1 text-primary"></i> Tema Activo:
                                </label>
                                <div className="d-flex align-items-center gap-2">
                                    <select
                                        className="form-select fw-bold border-primary shadow-sm"
                                        value={selectedThemeId || ''}
                                        onChange={handleActiveThemeSelectionChange}
                                        disabled={isMatchActive}
                                    >
                                        <option value="">-- Selecciona un tema --</option>
                                        {availableThemesList.map((themeOption) => (
                                            <option key={themeOption.id} value={themeOption.id}>
                                                {themeOption.name}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary fw-bold text-nowrap"
                                        onClick={handleOpenEditThemeModal}
                                        disabled={!selectedThemeId || selectedThemeId <= 0}
                                        title="Editar tema seleccionado"
                                    >
                                        <i className="bi bi-pencil-square"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success fw-bold text-nowrap"
                                        onClick={handleOpenCreateThemeModal}
                                        title="Crear nuevo tema"
                                    >
                                        <i className="bi bi-plus-lg"></i>
                                    </button>
                                </div>
                            </div>

                            {/* Selectores de duración, modo y física */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold small text-muted">
                                        <i className="bi bi-clock-history me-1 text-primary"></i> Duración de Ronda:
                                    </label>
                                    <select
                                        className="form-select fw-bold"
                                        value={roomConfigState.roundDuration}
                                        onChange={(e) => handleConfigParameterChange('roundDuration', Number(e.target.value))}
                                        disabled={isMatchActive}
                                    >
                                        <option value={5}>5 Segundos (Prueba ultra rápida)</option>
                                        <option value={10}>10 Segundos</option>
                                        <option value={15}>15 Segundos (Recomendado)</option>
                                        <option value={20}>20 Segundos</option>
                                        <option value={30}>30 Segundos</option>
                                        <option value={60}>60 Segundos</option>
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-bold small text-muted">
                                        <i className="bi bi-controller text-danger me-1"></i> Modo de Juego:
                                    </label>
                                    <select
                                        className="form-select fw-bold border-danger shadow-sm"
                                        value={roomConfigState.gameMode}
                                        onChange={(e) => handleConfigParameterChange('gameMode', e.target.value)}
                                        disabled={isMatchActive}
                                    >
                                        <option value="Timed">⏱️ Tiempo Fijo (Arrastre por tiempo)</option>
                                        <option value="SuddenDeath">⚡ Muerte Súbita K.O. (Hasta caer al lodo)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Opciones Avanzadas (Física y Muerte Súbita) */}
                            <div className="card border mb-3 shadow-sm rounded-3 overflow-hidden">
                                <button
                                    type="button"
                                    className="btn btn-light w-100 text-start d-flex justify-content-between align-items-center p-3 fw-bold small text-dark border-0 shadow-none"
                                    onClick={() => setIsAdvancedConfigOpen((prev) => !prev)}
                                    style={{ background: '#f8f9fa' }}
                                >
                                    <span>
                                        <i className="bi bi-sliders text-primary me-2"></i>
                                        Física de Soga y Prórroga (Avanzado)
                                    </span>
                                    <i className={`bi bi-chevron-${isAdvancedConfigOpen ? 'up' : 'down'} text-muted`}></i>
                                </button>
                                {isAdvancedConfigOpen && (
                                    <div className="p-3 bg-white border-top">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label small fw-bold text-muted mb-1">
                                                    Estilo de Desplazamiento (Física de Cuerda):
                                                </label>
                                                <select
                                                    className="form-select form-select-sm fw-bold"
                                                    value={roomConfigState.displacementStyle}
                                                    onChange={(e) => handleConfigParameterChange('displacementStyle', e.target.value)}
                                                    disabled={isMatchActive}
                                                >
                                                    <option value="DynamicBias">⚖️ Ponderado per cápita directo (Suave)</option>
                                                    <option value="VisualSuspense">🎢 Tensión dinámica (Suspenso exponencial)</option>
                                                </select>
                                            </div>
                                            <div className="col-md-6 d-flex align-items-center">
                                                <div className="form-check form-switch mt-3">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="toggleEnableOvertime"
                                                        checked={roomConfigState.enableOvertimeOnDraw}
                                                        onChange={(e) => handleConfigParameterChange('enableOvertimeOnDraw', e.target.checked)}
                                                        disabled={isMatchActive}
                                                    >
                                                    </input>
                                                    <label className="form-check-label small fw-bold" htmlFor="toggleEnableOvertime">
                                                        Activar Muerte Súbita automática si hay empate al finalizar tiempo
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Botones de acción de juego */}
                            <div className="row g-2">
                                <div className="col-md-5">
                                    <button
                                        type="button"
                                        className="btn btn-success fw-bold w-100 py-2 shadow-sm text-white"
                                        onClick={handleStartGameAction}
                                        disabled={isMatchActive}
                                    >
                                        <i className="bi bi-play-fill fs-5 me-1"></i> Iniciar Partida (3, 2, 1)
                                    </button>
                                </div>
                                <div className="col-md-4">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger fw-bold w-100 py-2 shadow-sm"
                                        onClick={handleConfirmFinishEarly}
                                        disabled={
                                            currentGameState.state !== 'playing' &&
                                            currentGameState.state !== 'paused' &&
                                            currentGameState.state !== 'sudden_death_critical'
                                        }
                                    >
                                        <i className="bi bi-stop-circle-fill me-1"></i> Finalizar Antes
                                    </button>
                                </div>
                                <div className="col-md-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-warning text-dark fw-bold w-100 py-2 shadow-sm"
                                        onClick={handleConfirmResetMatch}
                                        disabled={currentGameState.state === 'countdown'}
                                    >
                                        <i className="bi bi-arrow-counterclockwise me-1"></i> Reiniciar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 2: MONITOR DE TELEMETRÍA EN TIEMPO REAL (100MS)         */}
            {/* ============================================================ */}
            <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
                <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                    <h5 className="m-0 fw-bold">
                        <i className="bi bi-activity text-success me-2"></i> 3. Monitor de Telemetría en Vivo (100ms)
                    </h5>
                    <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-secondary">
                            Fans Conectados:{' '}
                            <strong className="text-warning">
                                {currentGameState.activeFansJ1 + currentGameState.activeFansJ2}
                            </strong>
                        </span>
                    </div>
                </div>
                <div className="card-body bg-white p-4">
                    {/* Timer Central y Estado */}
                    <div className="text-center mb-4">
                        <span className="small text-uppercase fw-bold text-muted d-block">
                            {currentGameState.state === 'sudden_death_critical' ? '⚡ Muerte Súbita Activa' : 'Tiempo Restante'}
                        </span>
                        <h1 className="fw-bold display-4 text-dark m-0 font-monospace">
                            {currentGameState.remainingSeconds !== undefined && currentGameState.remainingSeconds !== null
                                ? `${currentGameState.remainingSeconds.toFixed(1)}s`
                                : '0.0s'}
                        </h1>
                        <div className="progress mt-2 mx-auto" style={{ height: '8px', maxWidth: '400px' }}>
                            <div
                                className="progress-bar bg-warning progress-bar-striped progress-bar-animated"
                                style={{
                                    width: `${Math.min(
                                        100,
                                        ((currentGameState.remainingSeconds || 0) / (roomConfigState.roundDuration || 15)) * 100
                                    )}%`
                                }}
                            ></div>
                        </div>
                    </div>

                    {/* Barra Visual de Balance de Fuerza / Posición de la Soga */}
                    <div className="mb-4 p-3 bg-light rounded-4 border">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="fw-bold small" style={{ color: team1Color }}>
                                <i className="bi bi-arrow-left-circle-fill me-1"></i> {team1Name}: {pullProportions.percentage1}%
                            </span>
                            <span className="badge bg-dark">
                                Desplazamiento Soga: {currentGameState.ropeDisplacementVw?.toFixed(1) || 0} vw
                            </span>
                            <span className="fw-bold small" style={{ color: team2Color }}>
                                {team2Name}: {pullProportions.percentage2}% <i className="bi bi-arrow-right-circle-fill ms-1"></i>
                            </span>
                        </div>
                        <div className="position-relative" style={{ height: '26px' }}>
                            <div className="progress h-100 rounded-pill overflow-hidden shadow-inner">
                                <div
                                    className="progress-bar fw-bold"
                                    style={{
                                        width: `${pullProportions.percentage1}%`,
                                        backgroundColor: team1Color,
                                        transition: 'width 0.15s ease-out'
                                    }}
                                >
                                    {pullProportions.percentage1 > 20 && `${pullProportions.percentage1}%`}
                                </div>
                                <div
                                    className="progress-bar fw-bold"
                                    style={{
                                        width: `${pullProportions.percentage2}%`,
                                        backgroundColor: team2Color,
                                        transition: 'width 0.15s ease-out'
                                    }}
                                >
                                    {pullProportions.percentage2 > 20 && `${pullProportions.percentage2}%`}
                                </div>
                            </div>
                            {/* Marcador central del charco de lodo */}
                            <div
                                className="position-absolute top-50 start-50 translate-middle bg-dark border border-white rounded-circle shadow"
                                style={{ width: '16px', height: '16px', zIndex: 5 }}
                                title="Centro / Charco de Lodo"
                            ></div>
                        </div>
                    </div>

                    {/* Comparativa Equipo 1 vs Equipo 2 */}
                    <div className="row align-items-center g-3">
                        {/* EQUIPO 1 */}
                        <div className="col-md-5">
                            <div
                                className="p-3 rounded-4 border shadow-sm h-100"
                                style={{
                                    borderLeft: `8px solid ${team1Color}`,
                                    background: '#f8fafc'
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <h5 className="fw-bold m-0" style={{ color: team1Color }}>
                                        <i className="bi bi-shield-shaded me-1"></i> {team1Name}
                                    </h5>
                                    <span className="badge bg-dark">
                                        {currentGameState.activeFansJ1} aficionado(s)
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-3 my-2">
                                    {activeThemeObject?.player1Phase1Avatar && (
                                        <img
                                            src={resolveMediaUrl(
                                                tierJ1 === 3
                                                    ? (activeThemeObject.player1Phase3Avatar || activeThemeObject.player1Phase1Avatar)
                                                    : tierJ1 === 2
                                                        ? (activeThemeObject.player1Phase2Avatar || activeThemeObject.player1Phase1Avatar)
                                                        : activeThemeObject.player1Phase1Avatar
                                            )}
                                            alt="Avatar E1"
                                            className="rounded-circle border p-1 bg-white"
                                            style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    )}
                                    <div>
                                        <span className="small text-muted d-block">Puntaje Promedio (Per Cápita):</span>
                                        <h3 className="fw-bold m-0 text-dark">
                                            {currentGameState.scoreJ1?.toFixed(2)} pts
                                        </h3>
                                        <span className="small text-secondary d-block">
                                            Taps Totales: <strong>{currentGameState.totalTapsJ1}</strong>
                                        </span>
                                        <span className="badge bg-info text-dark mt-1">
                                            {tierJ1 === 1 ? '⚡ Fase 1: Energía Alta' : tierJ1 === 2 ? '💦 Fase 2: Sudor / Tensión' : '⚠️ Fase 3: Al borde del lodo'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* VS CENTRAL */}
                        <div className="col-md-2 text-center">
                            <span className="badge bg-dark fs-4 px-3 py-2 rounded-pill shadow">VS</span>
                        </div>

                        {/* EQUIPO 2 */}
                        <div className="col-md-5">
                            <div
                                className="p-3 rounded-4 border shadow-sm h-100"
                                style={{
                                    borderRight: `8px solid ${team2Color}`,
                                    background: '#f8fafc'
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <span className="badge bg-dark">
                                        {currentGameState.activeFansJ2} aficionado(s)
                                    </span>
                                    <h5 className="fw-bold m-0" style={{ color: team2Color }}>
                                        {team2Name} <i className="bi bi-shield-shaded ms-1"></i>
                                    </h5>
                                </div>
                                <div className="d-flex align-items-center justify-content-end gap-3 my-2 text-end">
                                    <div>
                                        <span className="small text-muted d-block">Puntaje Promedio (Per Cápita):</span>
                                        <h3 className="fw-bold m-0 text-dark">
                                            {currentGameState.scoreJ2?.toFixed(2)} pts
                                        </h3>
                                        <span className="small text-secondary d-block">
                                            Taps Totales: <strong>{currentGameState.totalTapsJ2}</strong>
                                        </span>
                                        <span className="badge bg-danger mt-1">
                                            {tierJ2 === 1 ? '⚡ Fase 1: Energía Alta' : tierJ2 === 2 ? '💦 Fase 2: Sudor / Tensión' : '⚠️ Fase 3: Al borde del lodo'}
                                        </span>
                                    </div>
                                    {activeThemeObject?.player2Phase1Avatar && (
                                        <img
                                            src={resolveMediaUrl(
                                                tierJ2 === 3
                                                    ? (activeThemeObject.player2Phase3Avatar || activeThemeObject.player2Phase1Avatar)
                                                    : tierJ2 === 2
                                                        ? (activeThemeObject.player2Phase2Avatar || activeThemeObject.player2Phase1Avatar)
                                                        : activeThemeObject.player2Phase1Avatar
                                            )}
                                            alt="Avatar E2"
                                            className="rounded-circle border p-1 bg-white"
                                            style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 3: GESTOR DE TEMAS Y PERSONALIZACIÓN                   */}
            {/* ============================================================ */}
            <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
                <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                    <h5 className="m-0 fw-bold">
                        <i className="bi bi-palette-fill text-info me-2"></i> 4. Gestor de Temas
                    </h5>
                    <button
                        type="button"
                        className="btn btn-sm btn-success fw-bold"
                        onClick={handleOpenCreateThemeModal}
                    >
                        <i className="bi bi-plus-circle me-1"></i> Crear Nuevo Tema
                    </button>
                </div>
                <div className="card-body bg-light p-4">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Temas Disponibles:</label>
                            <select
                                className="form-select fw-bold border-primary shadow-sm"
                                value={selectedThemeId || ''}
                                onChange={handleActiveThemeSelectionChange}
                            >
                                <option value="">-- Selecciona un tema --</option>
                                {availableThemesList.map((themeItem) => (
                                    <option key={themeItem.id} value={themeItem.id}>
                                        {themeItem.name} {themeItem.id === selectedThemeId ? '⭐ (Activo)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6 d-flex gap-2 justify-content-md-end flex-wrap pt-md-4">
                            <button
                                type="button"
                                className="btn btn-primary fw-bold shadow-sm"
                                onClick={handleOpenEditThemeModal}
                                disabled={!selectedThemeId || selectedThemeId <= 0}
                            >
                                <i className="bi bi-pencil-square me-1"></i> Editar Tema
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-bold shadow-sm"
                                onClick={handleCloneSelectedTheme}
                                disabled={!selectedThemeId || selectedThemeId <= 0}
                            >
                                <i className="bi bi-copy me-1"></i> Clonar
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-danger fw-bold shadow-sm"
                                onClick={handleConfirmDeleteTheme}
                                disabled={!selectedThemeId || selectedThemeId <= 0 || availableThemesList.length <= 1}
                            >
                                <i className="bi bi-trash-fill me-1"></i> Eliminar
                            </button>
                        </div>
                    </div>

                    {/* Previsualización del Tema Activo */}
                    {activeThemeObject && (
                        <div className="mt-3 p-3 bg-white rounded-3 border shadow-sm">
                            <div className="row g-2 align-items-center">
                                <div className="col-auto">
                                    {activeThemeObject.brandLogoUrl && (
                                        <img
                                            src={resolveMediaUrl(activeThemeObject.brandLogoUrl)}
                                            alt="Logo Brand"
                                            style={{ maxHeight: '40px' }}
                                        />
                                    )}
                                    {activeThemeObject.sponsorLogoUrl && (
                                        <img
                                            src={resolveMediaUrl(activeThemeObject.sponsorLogoUrl)}
                                            alt="Logo Sponsor"
                                            className="ms-2"
                                            style={{ maxHeight: '40px' }}
                                        />
                                    )}
                                </div>
                                <div className="col">
                                    <strong className="text-dark">{activeThemeObject.name}</strong>
                                    <div className="small text-muted">
                                        Portada: <strong>{activeThemeObject.coverTitle || '¡ESTIRA Y AFLOJA!'}</strong> | Equipos:{' '}
                                        <span className="badge" style={{ backgroundColor: activeThemeObject.player1Color || '#0284c7' }}>
                                            {activeThemeObject.player1Name || 'EQUIPO 1'}
                                        </span>{' '}
                                        vs{' '}
                                        <span className="badge" style={{ backgroundColor: activeThemeObject.player2Color || '#ef4444' }}>
                                            {activeThemeObject.player2Name || 'EQUIPO 2'}
                                        </span>{' '}
                                        | Tipografía: <strong>{activeThemeObject.fontFamily || 'Bangers'}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 4: HISTORIAL DE PARTIDAS                               */}
            {/* ============================================================ */}
            <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
                <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                    <h5 className="m-0 fw-bold">
                        <i className="bi bi-clock-history text-secondary me-2"></i> 5. Historial de Partidas
                    </h5>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-light"
                        onClick={loadMatchesHistory}
                    >
                        <i className="bi bi-arrow-repeat me-1"></i> Actualizar Historial
                    </button>
                </div>
                <div className="card-body bg-white p-0">
                    <div className="table-responsive">
                        <table className="table table-striped table-hover align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th># Partida</th>
                                    <th>Fecha / Hora</th>
                                    <th>Modo</th>
                                    <th>Ganador</th>
                                    <th>Puntaje J1</th>
                                    <th>Puntaje J2</th>
                                    <th>Taps Totales</th>
                                    <th>Fans Activos</th>
                                    <th>Duración</th>
                                    <th>Notas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matchesHistoryList.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="text-center py-4 text-muted">
                                            No se han registrado partidas de Estira y Afloja en esta sala todavía.
                                        </td>
                                    </tr>
                                ) : (
                                    matchesHistoryList.map((matchItem, index) => (
                                        <tr key={matchItem.id || matchItem.matchId || index}>
                                            <td className="fw-bold font-monospace">
                                                {matchItem.matchId ? matchItem.matchId.substring(0, 8) : `#${matchItem.id || index + 1}`}
                                            </td>
                                            <td className="small text-muted">
                                                {matchItem.playedAt ? new Date(matchItem.playedAt).toLocaleString() : '-'}
                                            </td>
                                            <td>
                                                <span className="badge bg-secondary">
                                                    {matchItem.gameMode === 'SuddenDeath' ? 'Muerte Súbita' : 'Tiempo Fijo'}
                                                </span>
                                            </td>
                                            <td>
                                                {matchItem.winner === 'player1' || matchItem.winner === 'J1' || matchItem.winner === 'Equipo 1' ? (
                                                    <span className="badge bg-info text-dark fw-bold">
                                                        {matchItem.winner} 🏆
                                                    </span>
                                                ) : matchItem.winner === 'player2' || matchItem.winner === 'J2' || matchItem.winner === 'Equipo 2' ? (
                                                    <span className="badge bg-danger fw-bold">
                                                        {matchItem.winner} 🏆
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-warning text-dark fw-bold">
                                                        {matchItem.winner || 'Empate'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="fw-bold text-info">
                                                {Number(matchItem.scoreJ1 || 0).toFixed(2)} pts
                                            </td>
                                            <td className="fw-bold text-danger">
                                                {Number(matchItem.scoreJ2 || 0).toFixed(2)} pts
                                            </td>
                                            <td className="small">
                                                J1: {matchItem.totalTapsJ1 || 0} | J2: {matchItem.totalTapsJ2 || 0}
                                            </td>
                                            <td className="small">
                                                J1: {matchItem.activeFansJ1 || 0} | J2: {matchItem.activeFansJ2 || 0}
                                            </td>
                                            <td className="small font-monospace">
                                                {matchItem.effectiveDurationSeconds || matchItem.durationSeconds || 15}s
                                            </td>
                                            <td>
                                                {matchItem.wasSuddenDeathTriggered && (
                                                    <span className="badge bg-warning text-dark me-1" title="Muerte Súbita activada">
                                                        ⚡ K.O.
                                                    </span>
                                                )}
                                                {matchItem.wasEmergencyStopped && (
                                                    <span className="badge bg-danger" title="Finalizada antes de tiempo">
                                                        🛑 Parada
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </SecondaryBase>
    );
};

TugOfWar.propTypes = {
    getOutOfTheDynamic: PropTypes.func.isRequired,
    roomId: PropTypes.string
};

export const EstiraYAfloja = TugOfWar;
export default TugOfWar;
